import type { Role } from "@/services/types/auth";

/** 角色 emoji + 描述（canvas page-85/87）*/
export const ROLE_META: Record<Role, { emoji: string; desc: string }> = {
  tech_admin: { emoji: "🛡", desc: "系统超管 · 全平台 · 最高权限" },
  owner: { emoji: "👑", desc: "全权限 · Danger PIN" },
  op_manager: { emoji: "📊", desc: "团队管理 + 财务只读" },
  op_supervisor: { emoji: "👔", desc: "监管 OP + 审批" },
  finance: { emoji: "💰", desc: "财务 + 对账 + 开票" },
  account_manager: { emoji: "🤝", desc: "同业 B2B 客户对接" },
  op: { emoji: "👤", desc: "日常下单 + 调度" },
};
/** RBACROLE-1 · 自定义角色（code=custom_xxxxxx）统一用 🧩 */
export const roleEmoji = (code: string) =>
  code.startsWith("custom_") ? "🧩" : (ROLE_META[code as Role]?.emoji ?? "👤");

/** 模块中文名 + emoji + 业务分组（canvas page-86 权限矩阵）*/
export type ModuleGroup = "resource" | "business" | "ops" | "system";
export const MODULE_META: Record<string, { cn: string; emoji: string; group: ModuleGroup }> = {
  hotels: { cn: "酒店", emoji: "🏨", group: "resource" },
  vehicles: { cn: "车辆", emoji: "🚐", group: "resource" },
  attractions: { cn: "门票", emoji: "🎫", group: "resource" },
  suppliers: { cn: "供应商", emoji: "📋", group: "resource" },
  products: { cn: "产品", emoji: "🎯", group: "resource" },
  orders: { cn: "订单", emoji: "🛒", group: "business" },
  customers: { cn: "CRM 中心", emoji: "👥", group: "business" },
  self_pay: { cn: "自费销售", emoji: "💵", group: "ops" },
  pricing: { cn: "定价", emoji: "💲", group: "business" },
  finance: { cn: "财务", emoji: "💼", group: "business" },
  channel_partners: { cn: "同业 B2B", emoji: "🤝", group: "business" },
  dispatch: { cn: "调度", emoji: "🚦", group: "ops" },
  driver: { cn: "司导", emoji: "🧑‍✈️", group: "ops" },
  vehicle_ops: { cn: "车务台账", emoji: "🔧", group: "ops" },
  notifications: { cn: "通知", emoji: "🔔", group: "ops" },
  analytics: { cn: "运营总览", emoji: "📊", group: "ops" },
  auth: { cn: "认证", emoji: "🔐", group: "system" },
  tenant: { cn: "租户", emoji: "🏢", group: "system" },
  system: { cn: "系统", emoji: "⚙️", group: "system" },
  api_integration: { cn: "API 集成", emoji: "🔌", group: "system" },
  audit: { cn: "审计", emoji: "📜", group: "system" },
  // RBAC-ENF1 新增权限模块
  drivers: { cn: "司导管理", emoji: "🚗", group: "resource" },
  routes: { cn: "路线与班表", emoji: "🛣️", group: "ops" },
  listing: { cn: "平台上架", emoji: "📤", group: "ops" },
  inventory: { cn: "库存", emoji: "📦", group: "ops" },
  complaints: { cn: "客诉", emoji: "📣", group: "ops" },
  handover: { cn: "OP 交接墙", emoji: "📝", group: "business" },
};

/** RBAC-ENF1 · 后端 /rbac/permissions 返回的中文名（权限点 / 模块）——优先于前端推导，新增权限点自动有中文名。 */
let SERVER_PERM_LABELS: Record<string, string> = {};
let SERVER_MODULE_LABELS: Record<string, string> = {};
export function registerServerLabels(
  perms: { code: string; module: string; label_cn?: string | null; module_label_cn?: string | null }[],
): void {
  const p: Record<string, string> = {};
  const m: Record<string, string> = {};
  for (const x of perms) {
    if (x.label_cn) p[x.code] = x.label_cn;
    if (x.module_label_cn) m[x.module] = x.module_label_cn;
  }
  SERVER_PERM_LABELS = p;
  SERVER_MODULE_LABELS = m;
}
export const moduleCn = (m: string) => MODULE_META[m]?.cn ?? SERVER_MODULE_LABELS[m] ?? m;
export const moduleEmoji = (m: string) => MODULE_META[m]?.emoji ?? "📦";
export const moduleGroup = (m: string): ModuleGroup => MODULE_META[m]?.group ?? "system";

export const GROUP_LABEL: Record<"all" | ModuleGroup, string> = {
  all: "全部",
  resource: "🏨 资源类",
  business: "📋 商务类",
  ops: "🛠 运营类",
  system: "⚙ 系统类",
};

/** 角色在某模块的权限等级（canvas page-86：从该角色在该模块的权限码推导）*/
export type PermLevel = "edit" | "read" | "none";
const WRITE_RE = /\.(create|update|delete|manage|process|approve|assign|assign_driver|run|generate|cancel|issue|refund|void|grant|revoke|reset|settle|import|export_pii)$/;
export type PermKind = "read" | "write";
/** 权限码是不是「写」：以后端逐条标的 kind 为准（RBAC-UX-1）；后端没给 kind（旧后端）才退回按末尾动作猜的旧正则。 */
export function isWriteCode(code: string, kindOf?: (c: string) => PermKind | null | undefined): boolean {
  const k = kindOf?.(code);
  return k ? k === "write" : WRITE_RE.test(code);
}
export function deriveLevel(codesInModule: string[], kindOf?: (c: string) => PermKind | null | undefined): PermLevel {
  if (codesInModule.length === 0) return "none";
  if (codesInModule.some((c) => isWriteCode(c, kindOf))) return "edit";
  return "read";
}
export const LEVEL_META: Record<PermLevel, { icon: string; label: string; cls: string }> = {
  edit: { icon: "✏️", label: "可编辑", cls: "text-emerald-600" },
  read: { icon: "👁", label: "只读", cls: "text-sky-500" },
  none: { icon: "—", label: "不可见", cls: "text-slate-300" },
};

/** 权限码 → 中文可读名（前端推导·后端权限无中文 label）。code = module.resource.action */
const ACTION_CN: Record<string, string> = {
  list: "查看列表", view: "查看", create: "新建", update: "修改", delete: "删除",
  export: "导出", manage: "管理", approve: "审批", assign: "分配", assign_driver: "派司导",
  scan: "扫码", submit: "提交", request: "申请", report: "上报", sync: "同步", record: "记录",
  reconcile: "对账", settle: "结算", refund: "退款", refund_small: "小额退款", cancel: "取消",
  confirm: "确认", reassign: "改派", override: "强制改派", publish: "发布", run: "执行",
  generate: "生成", schedule: "排班", preview: "预览", manage_schedule: "排期管理",
  team_view: "团队视图", approve_yellow: "黄牌审批", quota_approve: "额度审批",
  channel_markup: "渠道加价", holiday_manage: "节假日管理", send_manual: "手动发送",
  send_voucher: "发凭证", assign_driver_schedule: "司导排班", driver_schedule: "司导排班",
};
const RESOURCE_CN: Record<string, string> = {
  hotels: "酒店", room_types: "房型", inventory: "库存", pricing: "价格", rooms: "房间",
  vehicles: "车辆", licenses: "证照", maintenance: "维保",
  attractions: "景点", ticket_types: "票种", suppliers: "供应商",
  products: "产品", schedules: "排期", orders: "订单", channel_partners: "同业客户",
  dispatch: "调度", drivers: "司导", driver: "司导", users: "用户", roles: "角色",
  permission_matrix: "权限矩阵", supervision: "监管", finance: "财务", invoices: "发票",
  payments: "收款", reports: "报表", qb: "QuickBooks", expenses: "费用", leave: "请假",
  emergency: "紧急", today: "今日", checkpoints: "打卡", analytics: "运营总览",
  dashboard: "看板", audit: "审计", logs: "日志", platforms: "平台", sync_log: "同步日志",
  crawl: "抓取", notifications: "通知", announcement: "公告", system: "系统", health: "健康",
  tenant: "租户", vehicle_ops: "车务", settlements: "结算",
  customers: "客户", catalog: "项目目录", sales: "成交记录", credentials: "凭据", quotas: "额度",
};

export function permLabel(code: string): string {
  if (SERVER_PERM_LABELS[code]) return SERVER_PERM_LABELS[code];
  const parts = code.split(".");
  const action = parts[parts.length - 1];
  const resource = parts.length >= 3 ? parts[1] : parts[0];
  const r = RESOURCE_CN[resource] ?? resource;
  const a = ACTION_CN[action] ?? action;
  return `${r} · ${a}`;
}
